// Screenshots and recordings on support requests: picking them (drop zone, file picker, thumbnails
// with edit/remove) and showing the ones already sent.
import { useEffect, useRef, useState, type DragEvent } from "react";
import { Film, Paperclip, PenLine, X as LinkedIn } from "lucide-react";
import { SUPPORT_ACCEPT, SUPPORT_ACCEPTED_SUMMARY, SUPPORT_MAX_FILE_BYTES, formatBytes, supportAttachmentUrl, type SupportAttachment } from "@ml/shared/support-files";
import { droppedFiles, isImage, mergeFiles } from "./attachment-rules";
import "./support-attachments.css";

/**
 * Selected files plus the friendly error from the last add. Every change reads the live selection
 * from a ref, so two adds in the same tick (a paste, then a drop) can never overwrite each other.
 */
export type AttachmentSelection = ReturnType<typeof useAttachmentSelection>;
export function useAttachmentSelection() {
  const [files, setState] = useState<File[]>([]);
  const [error, setError] = useState("");
  const live = useRef<File[]>([]);
  const update = (change: (current: File[]) => File[]) => {
    live.current = change(live.current);
    setState(live.current);
  };
  return {
    files,
    error,
    update,
    add(incoming: File[]) {
      if (!incoming.length) return;
      const merged = mergeFiles(live.current, incoming);
      update(() => merged.files);
      setError(merged.error);
    },
    remove(index: number) {
      update((current) => current.filter((_, i) => i !== index));
      setError("");
    },
    clear() {
      update(() => []);
      setError("");
    },
  };
}

/** Drag handlers for any element that should accept dropped files (a textarea, a drop zone). */
export function useFileDrop(onFiles: (files: File[]) => void, disabled?: boolean) {
  const [over, setOver] = useState(false);
  const accepts = (e: DragEvent) => !disabled && Array.from(e.dataTransfer?.types ?? []).includes("Files");
  return {
    over,
    props: {
      onDragOver(e: DragEvent) {
        if (!accepts(e)) return;
        e.preventDefault();
        e.dataTransfer.dropEffect = "copy";
        setOver(true);
      },
      onDragLeave() {
        setOver(false);
      },
      onDrop(e: DragEvent) {
        setOver(false);
        if (!accepts(e)) return;
        e.preventDefault();
        onFiles(droppedFiles(e.dataTransfer));
      },
    },
  };
}

function Thumb({ file }: { file: File }) {
  const [url, setUrl] = useState("");
  useEffect(() => {
    if (!isImage(file)) return;
    const u = URL.createObjectURL(file);
    setUrl(u);
    return () => URL.revokeObjectURL(u);
  }, [file]);
  return url ? <img src={url} alt="" /> : <Film size={18} aria-hidden />;
}

/**
 * The attachment area of a report or reply. `zone` shows the full drop zone; without it, just an
 * Attach button and the chips (for the reply composer, whose textarea is the drop target).
 */
export function AttachmentPicker({
  files,
  error,
  onAdd,
  onRemove,
  onEdit,
  disabled,
  zone = true,
}: {
  files: File[];
  error?: string;
  onAdd: (files: File[]) => void;
  onRemove: (index: number) => void;
  onEdit?: (index: number) => void;
  disabled?: boolean;
  zone?: boolean;
}) {
  const input = useRef<HTMLInputElement>(null);
  const drop = useFileDrop(onAdd, disabled);
  const pick = (
    <button type="button" className="gf-attach__pick" disabled={disabled} onClick={() => input.current?.click()}>
      <Paperclip size={14} /> {zone ? "Attach screenshots or a recording" : "Attach"}
    </button>
  );
  return (
    <div className="gf-attach">
      {zone ? (
        <div className={`gf-attach__zone${drop.over ? " is-over" : ""}`} {...drop.props}>
          {pick}
          <small>Drop files here or paste into the box above — {SUPPORT_ACCEPTED_SUMMARY}, up to {formatBytes(SUPPORT_MAX_FILE_BYTES)} each.</small>
        </div>
      ) : (
        pick
      )}
      <input
        ref={input}
        type="file"
        multiple
        hidden
        accept={SUPPORT_ACCEPT}
        onChange={(e) => {
          onAdd(Array.from(e.target.files ?? []));
          // Reset so picking the same file again still fires.
          e.target.value = "";
        }}
      />
      {files.length > 0 && (
        <ul className="gf-attach__chips" aria-label="Attached files">
          {files.map((file, i) => (
            <li key={`${file.name}-${file.size}-${i}`}>
              <span className="gf-attach__thumb">
                <Thumb file={file} />
              </span>
              <span className="gf-attach__name">
                <span>{file.name}</span>
                <small>{formatBytes(file.size)}</small>
              </span>
              {onEdit && isImage(file) && (
                <button type="button" disabled={disabled} onClick={() => onEdit(i)} aria-label={`Crop or black out ${file.name}`} title="Crop, black out or mark up">
                  <PenLine size={14} />
                </button>
              )}
              <button type="button" disabled={disabled} onClick={() => onRemove(i)} aria-label={`Remove ${file.name}`} title="Remove">
                <LinkedIn size={14} />
              </button>
            </li>
          ))}
        </ul>
      )}
      {error && (
        <p role="alert" className="gf-attach__error">
          {error}
        </p>
      )}
    </div>
  );
}

/** Attachments already sent: images open full size in a new tab, recordings play in place. */
export function SupportAttachments({ items }: { items?: SupportAttachment[] }) {
  if (!items?.length) return null;
  return (
    <ul className="gf-attach__gallery" aria-label="Attachments">
      {items.map((a) => {
        const url = supportAttachmentUrl(a);
        return (
          <li key={a.id}>
            {a.mime.startsWith("video/") ? (
              <video src={url} controls preload="metadata" aria-label={a.name} />
            ) : (
              <a href={url} target="_blank" rel="noopener noreferrer" title={`${a.name} — open full size`}>
                <img src={url} alt={a.name} loading="lazy" />
              </a>
            )}
          </li>
        );
      })}
    </ul>
  );
}
